import { Icons } from '@/assets/icons/icons';
import { Badge } from '@/components/ui/Badge';
import { XStack } from '@/components/ui/layout/layout';
import { Tooltip } from '@/components/ui/Tooltip';

import { useEditPersonMutation } from './edits.query';
import { InlineTextField } from './inline-text-field';

import type { WikiPersonDetail } from '../people-wiki.types';

const sameName = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase();

/** The other names of a person. The names that the page holds are chips that can be removed, and one field adds a
 * name. The names that come from the person's accounts are shown after them and cannot be changed here. */
export const AliasesEditor = ({ person }: { person: WikiPersonDetail }) => {
  const {
    mutate: editPerson,
    variables: pendingChanges,
    isPending,
  } = useEditPersonMutation(person.id);
  // A name that is on its way out stays hidden until the page answers, so it does not come back for a moment
  const removing = isPending ? (pendingChanges?.remove_aliases ?? []) : [];
  const ownAliases = person.ownAliases.filter(
    (own) => !removing.some((alias) => sameName(alias, own))
  );
  const accountNames = person.aliases.filter(
    (alias) => !person.ownAliases.some((own) => sameName(own, alias))
  );

  return (
    <XStack wrap className="items-center gap-1.5 text-xs">
      <span className="text-foreground-subtle">Also known as</span>
      {ownAliases.map((alias) => (
        <Badge
          key={alias}
          variant="secondary"
          className="gap-1 pr-1 font-normal text-foreground-muted"
        >
          {alias}
          <button
            type="button"
            aria-label={`Remove ${alias}`}
            className="inline-flex size-4 items-center justify-center rounded-sm hover:bg-secondary-hover focus-ring"
            onClick={() => editPerson({ remove_aliases: [alias] })}
          >
            <Icons.cancel className="size-3" />
          </button>
        </Badge>
      ))}
      {accountNames.map((alias) => (
        <Tooltip
          key={alias}
          label="Comes from their accounts"
          side="top"
          delayDuration={200}
        >
          <span className="cursor-default text-foreground-subtle">{alias}</span>
        </Tooltip>
      ))}
      <InlineTextField
        // A new key empties the field after a name is added
        key={person.ownAliases.join('\n')}
        value=""
        label="Add another name"
        placeholder="Add a name"
        maxLength={255}
        className="w-28 text-xs"
        onSave={(alias, revert) => {
          if (person.ownAliases.some((own) => sameName(own, alias))) {
            revert();
            return;
          }
          // The typed name stays in the field while it is saved, and after a failure, so it can be tried again
          editPerson({ add_aliases: [alias] }, { onSuccess: revert });
        }}
      />
    </XStack>
  );
};
